import type {RefObject} from 'react'
import {
  AGENT_TASKS_REASONING_EFFORT_KEY,
  AGENT_TASKS_SELECTED_MODEL_KEY,
} from '@github-ui/one-copilot-composer/storage-keys'
import type {HandleUserSubmit, HandleUserSubmitOptions} from '@github-ui/one-copilot-composer/user-submit'
import {isReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'

function parseMcpPermissionMode(mode: unknown): McpPermissionMode | undefined {
  return mode === 'default' || mode === 'allow_all' ? mode : undefined
}

export async function submitTask(message: string, handleUserSubmitRef: RefObject<HandleUserSubmit>): Promise<void> {
  let parsed
  try {
    parsed = JSON.parse(message)
  } catch {
    await handleUserSubmitRef.current(message, null, '', {})
    return
  }

  if (parsed.text === undefined) {
    await handleUserSubmitRef.current(message, null, '', {})
    return
  }

  const selectedRepo = parsed.selectedRepo
    ? {
        ...parsed.selectedRepo,
        private: parsed.selectedRepo.visibility === 'private',
      }
    : null
  const selectedBranch = parsed.selectedBranch || ''
  const selectedAgent = parsed.selectedAgent
  const mcpPermissionMode =
    parseMcpPermissionMode(parsed.mcpPermissionMode) ?? parseMcpPermissionMode(parsed.mcp_permission_mode)

  let modelId = parsed.model?.id
  const suppressStoredReasoningEffort = parsed.reasoning_effort === null
  let reasoningEffort = isReasoningEffort(parsed.reasoning_effort) ? parsed.reasoning_effort : undefined

  try {
    if (!modelId) {
      modelId = localStorage.getItem(AGENT_TASKS_SELECTED_MODEL_KEY) ?? undefined
    }
    if (!suppressStoredReasoningEffort && !reasoningEffort) {
      const storedReasoningEffort = localStorage.getItem(AGENT_TASKS_REASONING_EFFORT_KEY)
      reasoningEffort = isReasoningEffort(storedReasoningEffort) ? storedReasoningEffort : undefined
    }
  } catch {
    // localStorage might not be available (SSR, etc)
  }

  const references = Array.isArray(parsed.references)
    ? (parsed.references as unknown[]).filter((ref): ref is string => typeof ref === 'string')
    : []
  const textWithRefs = references.length > 0 ? `${parsed.text}\n\n${references.join('\n')}` : parsed.text

  const options = {
    ...(references.length > 0 && {references}),
    ...(reasoningEffort && {reasoningEffort}),
    ...(mcpPermissionMode && {mcpPermissionMode}),
  }

  const submitOptions: [] | [HandleUserSubmitOptions] = Object.keys(options).length > 0 ? [options] : []
  await handleUserSubmitRef.current(
    textWithRefs,
    selectedRepo,
    selectedBranch,
    selectedAgent || '',
    modelId,
    ...submitOptions,
  )
}
